import { useState, useEffect } from 'react'
import { getFullApiUrl } from '../../config/api';
import { Mail, RefreshCw, AlertCircle, CheckCircle } from 'lucide-react'

interface UnverifiedUser {
  email: string
  name: string
  createdAt: string
  verificationStatus: string
}

interface UnverifiedEmailsData {
  unverifiedUsers: UnverifiedUser[]
  count: number
  totalUsers: number
}

export function UnverifiedEmailsViewer() {
  const [data, setData] = useState<UnverifiedEmailsData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [resending, setResending] = useState<string | null>(null)
  const [resendSuccess, setResendSuccess] = useState<string | null>(null)

  const fetchData = async () => {
    try {
      setLoading(true)
      setError(null)
      const token = localStorage.getItem('authToken')
      const response = await fetch(getFullApiUrl(`/admin/unverified-emails`), {
        headers: { Authorization: `Bearer ${token}` }
      })
      
      if (!response.ok) throw new Error('Failed to fetch data')
      
      const result = await response.json()
      setData(result)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error')
    } finally {
      setLoading(false)
    }
  }

  const resendVerification = async (email: string) => {
    try {
      setResending(email)
      setResendSuccess(null)
      const token = localStorage.getItem('authToken')
      const response = await fetch(getFullApiUrl(`/admin/resend-verification`), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ email })
      })
      
      if (!response.ok) throw new Error('Failed to resend')
      
      setResendSuccess(email)
      setTimeout(() => setResendSuccess(null), 3000)
    } catch (err) {
      alert('Failed to resend verification email')
    } finally {
      setResending(null)
    }
  }

  useEffect(() => {
    fetchData()
  }, [])

  if (loading) {
    return <div className="text-center py-8">Loading...</div>
  }

  if (error) {
    return <div className="text-red-600 py-8">Error: {error}</div>
  }

  if (!data) return null

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">SES Email Verification Status</h2>
          <p className="text-gray-600 mt-1">
            {data.count} of {data.totalUsers} users have unverified emails
          </p>
        </div>
        <button
          onClick={fetchData}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
        >
          <RefreshCw className="w-4 h-4" />
          Refresh
        </button>
      </div>

      {data.count === 0 ? (
        <div className="bg-green-50 border border-green-200 rounded-lg p-6 text-center">
          <CheckCircle className="w-12 h-12 text-green-600 mx-auto mb-3" />
          <p className="text-green-800 font-medium">All users have verified emails!</p>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Registered</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Action</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {data.unverifiedUsers.map((user) => (
                <tr key={user.email}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {user.name}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                    {user.email}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                      <AlertCircle className="w-3 h-3" />
                      {user.verificationStatus}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                    {new Date(user.createdAt).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    {resendSuccess === user.email ? (
                      <span className="text-green-600 flex items-center gap-1">
                        <CheckCircle className="w-4 h-4" />
                        Sent!
                      </span>
                    ) : (
                      <button
                        onClick={() => resendVerification(user.email)}
                        disabled={resending === user.email}
                        className="flex items-center gap-1 text-blue-600 hover:text-blue-800 disabled:opacity-50"
                      >
                        <Mail className="w-4 h-4" />
                        {resending === user.email ? 'Sending...' : 'Resend'}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
